import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { Search, MapPin, Globe, Users, TrendingUp, Building2, Rocket, Sparkles, Twitter, Linkedin, Mail, Phone, ExternalLink, Calendar, Award, BookOpen, Image as ImageIcon, Package, Tag, Target, Cpu } from "lucide-react";
import MagazineProfile, { hasMagazine } from "@/components/MagazineProfile";
import { AddMyProfileCTA, Pagination } from "@/components/DirectoryHelpers";

const PAGE_SIZE = 9;

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_OPTIONS = ["K-12", "HigherEd", "Test-Prep", "Skilling", "Corporate", "Edu-Admins"];
const CATEGORY_OPTIONS = ["AI-Learning", "LMS", "Adaptive", "Content", "Assessment", "Tutoring", "Coding", "Language", "STEM", "Analytics", "AR-VR", "Marketplace"];
const STAGE_OPTIONS = ["Bootstrapped", "Pre-Seed", "Seed", "Series A", "Series B+", "Listed"];

function SectionHead({ icon: Icon, color = "sky", title, small = false }) {
  const map = {
    sky: "bg-sky-100 text-sky-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
    violet: "bg-violet-100 text-violet-700",
    slate: "bg-slate-200 text-slate-700",
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`grid place-items-center rounded-full ${map[color] || map.sky} ${small ? "w-8 h-8" : "w-10 h-10"}`}>
        <Icon className={small ? "w-4 h-4" : "w-5 h-5"}/>
      </div>
      <h2 className={`font-editorial font-bold text-slate-900 ${small ? "text-lg" : "text-2xl"}`}>{title}</h2>
    </div>
  );
}

const resolveImg = (src) => {
  if (!src) return "";
  return src.startsWith("http") ? src : `${API}${src}`;
};

export function EdtechListPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [audience, setAudience] = useState("");
  const [category, setCategory] = useState("");
  const [stage, setStage] = useState("");

  useEffect(() => { setPage(1); }, [q, audience, category, stage]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/edtech", { params: {
        search: q || undefined, audience: audience || undefined, category: category || undefined, stage: stage || undefined,
        limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE,
      }})
        .then(r => { setItems(r.data.items); setTotal(r.data.total); });
    }, 200);
    return () => clearTimeout(t);
  }, [q, audience, category, stage, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="edtech-list-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sky-700 mb-2"><Rocket className="w-4 h-4"/><span className="overline">EdTech Companies</span></div>
          <h1 className="font-editorial text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">India's education technology landscape.</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Discover the companies building the future of learning — from AI tutors and adaptive assessment platforms to LMS, coding schools and skilling ecosystems.</p>
        </div>
        <AddMyProfileCTA testId="edtech-cta"/>
      </div>

      {/* Filters */}
      <div className="mt-8 card-soft p-5 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 focus-within:border-sky-400">
          <Search className="w-4 h-4 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search companies, product, tagline…" className="flex-1 outline-none text-sm bg-transparent" data-testid="edtech-search"/>
        </div>
        <select value={audience} onChange={e => setAudience(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm" data-testid="edtech-filter-audience">
          <option value="">All audiences</option>
          {AUDIENCE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm" data-testid="edtech-filter-category">
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={stage} onChange={e => setStage(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm" data-testid="edtech-filter-stage">
          <option value="">Any stage</option>
          {STAGE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 text-xs text-slate-500 overline">{total} {total === 1 ? "company" : "companies"}</div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(c => (
          <Link key={c.id} to={`/edtech/${c.slug}`} className="card-soft p-5 hover:shadow-lg transition-shadow group" data-testid={`edtech-card-${c.id}`}>
            <div className="flex items-start gap-3">
              {c.logo ? (
                <img src={resolveImg(c.logo)} alt={c.name} className="w-14 h-14 rounded-xl object-cover border border-slate-100"/>
              ) : (
                <div className="w-14 h-14 rounded-xl bg-sky-100 grid place-items-center text-sky-700"><Building2 className="w-6 h-6"/></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-editorial text-lg font-bold text-slate-900 truncate group-hover:text-sky-800">{c.name}</h3>
                  {c.featured && <span className="text-[9px] overline px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800">featured</span>}
                </div>
                {c.tagline && <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{c.tagline}</div>}
              </div>
            </div>
            {(c.audience || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {c.audience.slice(0, 3).map(a => <span key={a} className="text-[10px] overline px-2 py-0.5 rounded-full tag-blue">{a}</span>)}
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
              {c.stage && <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3"/>{c.stage}</span>}
              {c.employees && <span className="flex items-center gap-1"><Users className="w-3 h-3"/>{c.employees}</span>}
              {c.headquarters && <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.headquarters}</span>}
            </div>
          </Link>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-16">No matching companies. Try adjusting filters.</div>}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage(p => Math.max(1, p - 1))}
        onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        testId="edtech-pagination"
      />
    </div>
  );
}

export function EdtechDetailPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const user = getUser();
  const isAdmin = user?.role === "admin" || user?.role === "editor";
  const reload = () => api.get(`/edtech/${slug}`).then(r => setC(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    setC(null); setNotFound(false);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Company not found. <Link to="/edtech" className="text-sky-700 underline">Browse all</Link></div>;
  if (!c) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</div>;

  const handleSave = async (fields) => {
    const payload = {
      ...c,
      name: fields.masthead || c.name,
      top_photos: fields.top_photos,
      col_left_html: fields.col_left_html,
      col_right_html: fields.col_right_html,
      masthead_year: fields.year,
    };
    await api.put(`/edtech/${c.id}`, payload);
    await reload();
  };

  if (hasMagazine(c) || isAdmin) {
    return (
      <MagazineProfile
        photos={c.top_photos || []}
        leftHtml={c.col_left_html}
        rightHtml={c.col_right_html}
        masthead={c.name}
        year={c.masthead_year || new Date().getFullYear()}
        filename={c.name}
        canEdit={isAdmin}
        onSave={handleSave}
        testId="edtech-magazine"
      />
    );
  }

  return (
    <div data-testid="edtech-detail-page" className="bg-gradient-to-b from-sky-50/30 via-white to-white">
      {/* Hero */}
      <div className="relative h-56 md:h-72 bg-slate-900">
        {c.banner && <img src={resolveImg(c.banner)} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-60"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 to-slate-900/40"/>
      </div>
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <div className="card-soft p-6 md:p-8 bg-white">
          <div className="flex flex-wrap items-start gap-5">
            {c.logo ? (
              <img src={resolveImg(c.logo)} alt={c.name} className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-slate-100"/>
            ) : (
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-sky-100 grid place-items-center text-sky-700"><Building2 className="w-10 h-10"/></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-editorial text-3xl md:text-4xl font-bold text-slate-900">{c.name}</h1>
                {c.featured && <span className="text-[10px] overline px-2 py-1 rounded-full bg-amber-50 text-amber-800 flex items-center gap-1"><Sparkles className="w-3 h-3"/>Featured</span>}
              </div>
              {c.tagline && <p className="text-slate-600 mt-1">{c.tagline}</p>}
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
                {c.founded_year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>Founded {c.founded_year}</span>}
                {c.headquarters && <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.headquarters}, {c.country}</span>}
                {c.stage && <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3"/>{c.stage}</span>}
                {c.employees && <span className="flex items-center gap-1"><Users className="w-3 h-3"/>{c.employees}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold flex items-center gap-1" data-testid="edtech-visit-btn"><Globe className="w-3.5 h-3.5"/>Visit site<ExternalLink className="w-3 h-3"/></a>}
              {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-700"><Linkedin className="w-4 h-4"/></a>}
              {c.twitter && <a href={c.twitter} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-slate-100 hover:bg-sky-50 text-slate-700"><Twitter className="w-4 h-4"/></a>}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {c.gallery?.length > 0 && (
            <section className="rounded-3xl bg-violet-50/50 border border-violet-100 p-6 md:p-8" data-testid="edtech-gallery">
              <SectionHead icon={ImageIcon} color="violet" title="Photo Gallery"/>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">{c.gallery.map((g, idx) => <div key={idx} className="aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-sm ring-1 ring-violet-100"><img src={g} alt="" className="w-full h-full object-cover"/></div>)}</div>
            </section>
          )}
          {(c.description_html || c.description) && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={BookOpen} color="sky" title={`About ${c.name}`}/>
              <div className="mt-5">
                {c.description_html
                  ? <div className="prose-editorial text-slate-800" dangerouslySetInnerHTML={{ __html: (c.description_html || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }} data-testid="edtech-description-html"/>
                  : <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{c.description}</p>}
              </div>
            </section>
          )}

          {(c.products || []).length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Package} color="emerald" title="Products"/>
              <div className="flex flex-wrap gap-2 mt-5">{c.products.map(p => <span key={p} className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-800 text-xs font-semibold shadow-sm">{p}</span>)}</div>
            </section>
          )}

          {(c.categories || []).length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Tag} color="amber" title="Categories"/>
              <div className="flex flex-wrap gap-2 mt-5">{c.categories.map(cat => <span key={cat} className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-amber-800 text-xs font-semibold shadow-sm">{cat}</span>)}</div>
            </section>
          )}

          {(c.audience || []).length > 0 && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Target} color="rose" title="Target audience"/>
              <div className="flex flex-wrap gap-2 mt-5">{c.audience.map(a => <span key={a} className="px-3 py-1.5 rounded-full bg-white border border-rose-200 text-rose-700 text-xs font-semibold shadow-sm">{a}</span>)}</div>
            </section>
          )}

          {((c.integrations || []).length > 0 || (c.tech_stack || []).length > 0) && (
            <section className="rounded-3xl p-6 md:p-8" style={{ background: "#FBF6E4", border: "1px solid #F0E6C8" }}>
              <SectionHead icon={Cpu} color="slate" title="Tech & Integrations"/>
              {(c.tech_stack || []).length > 0 && (
                <div className="mt-5 mb-4">
                  <div className="overline text-xs text-slate-500 mb-2">Tech stack</div>
                  <div className="flex flex-wrap gap-2">{c.tech_stack.map(t => <span key={t} className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs">{t}</span>)}</div>
                </div>
              )}
              {(c.integrations || []).length > 0 && (
                <div className="mt-4">
                  <div className="overline text-xs text-slate-500 mb-2">Integrations</div>
                  <div className="flex flex-wrap gap-2">{c.integrations.map(i => <span key={i} className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs">{i}</span>)}</div>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="space-y-5">
          <div className="rounded-3xl p-6" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
            <SectionHead icon={Award} color="amber" title="Company snapshot" small/>
            <ul className="space-y-2 text-sm mt-4">
              {c.founded_year && <li className="flex justify-between"><span className="text-slate-500">Founded</span><span className="font-semibold text-slate-900">{c.founded_year}</span></li>}
              {c.stage && <li className="flex justify-between"><span className="text-slate-500">Stage</span><span className="font-semibold text-slate-900">{c.stage}</span></li>}
              {c.funding_total && <li className="flex justify-between"><span className="text-slate-500">Total funding</span><span className="font-semibold text-emerald-700">{c.funding_total}</span></li>}
              {c.valuation && <li className="flex justify-between"><span className="text-slate-500">Valuation</span><span className="font-semibold text-emerald-700">{c.valuation}</span></li>}
              {c.employees && <li className="flex justify-between"><span className="text-slate-500">Employees</span><span className="font-semibold text-slate-900">{c.employees}</span></li>}
              {c.headquarters && <li className="flex justify-between"><span className="text-slate-500">HQ</span><span className="font-semibold text-slate-900">{c.headquarters}</span></li>}
            </ul>
          </div>
          {(c.founders || []).length > 0 && (
            <div className="rounded-3xl p-6" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
              <SectionHead icon={Users} color="amber" title="Founders" small/>
              <ul className="text-sm space-y-1.5 mt-3">{c.founders.map(f => <li key={f} className="text-slate-800 flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"/>{f}</li>)}</ul>
            </div>
          )}
          {(c.investors || []).length > 0 && (
            <div className="rounded-3xl p-6" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
              <SectionHead icon={TrendingUp} color="amber" title="Investors" small/>
              <ul className="text-sm space-y-1.5 mt-3">{c.investors.map(i => <li key={i} className="text-slate-800 flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"/>{i}</li>)}</ul>
            </div>
          )}
          {(c.email || c.phone) && (
            <div className="rounded-3xl p-6" style={{ background: "#FEFAE0", border: "1px solid #FDE68A" }}>
              <SectionHead icon={Phone} color="amber" title="Contact" small/>
              <ul className="text-sm space-y-2 mt-3">
                {c.email && <li className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-sky-700"/><a href={`mailto:${c.email}`} className="hover:underline break-all">{c.email}</a></li>}
                {c.phone && <li className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-sky-700"/><a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></li>}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
